import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Layers, Plus, Pencil, Trash2, Move, ChevronDown, ChevronRight, Search, Languages, Globe2, CheckSquare, Square, Inbox,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const fmt = (v) => `${Math.round(Number(v || 0) * 100)}%`;

export default function Clusters() {
  const [clusters, setClusters] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [unassignedLangs, setUnassignedLangs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkTarget, setBulkTarget] = useState("");
  const [unassignedOpen, setUnassignedOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("");

  // Dialog states
  const [clusterDialog, setClusterDialog] = useState({ open: false, editing: null });
  const [clusterForm, setClusterForm] = useState({ name: "", country_ids: [] });
  const [saving, setSaving] = useState(false);

  const [langDialog, setLangDialog] = useState({ open: false, clusterId: null, mode: "new" });
  const [langForm, setLangForm] = useState({ name: "", iso_code: "", lang_status: "Active", start_date: "", end_date: "", details: "" });

  const [moveDialog, setMoveDialog] = useState({ open: false, projectId: null, currentCluster: null, projectName: "" });
  const [moveTarget, setMoveTarget] = useState("");

  const [unassigned, setUnassigned] = useState([]);
  const [assignPick, setAssignPick] = useState("");

  const load = async () => {
    setLoading(true);
    const [c, r, allProjects] = await Promise.all([
      api.get("/clusters"),
      api.get("/regions").then((rr) => rr.data),
      api.get("/language-projects", { params: { limit: 2000 } }).then((rr) => rr.data),
    ]);
    setClusters(c.data);
    setUnassignedLangs(allProjects.filter((p) => !p.cluster_id));
    // Get all countries via regions
    const allCountries = [];
    for (const region of r) {
      const cs = await api.get(`/regions/${region.id}/countries`).then((x) => x.data);
      cs.forEach((cc) => allCountries.push({ ...cc, region_name: region.name }));
    }
    setCountries(allCountries);
    setRegions(r);
    setSelected(new Set());
    setBulkTarget("");
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return clusters.filter((c) => {
      const rids = c.region_ids && c.region_ids.length ? c.region_ids : (c.region_id ? [c.region_id] : []);
      const cnames = c.country_names && c.country_names.length ? c.country_names : (c.country_name ? [c.country_name] : []);
      if (regionFilter && !rids.includes(regionFilter)) return false;
      if (search) {
        const s = search.toLowerCase();
        const cnBlob = cnames.join(" ").toLowerCase();
        if (!c.name.toLowerCase().includes(s) && !cnBlob.includes(s)) return false;
      }
      return true;
    });
  }, [clusters, regionFilter, search]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const expandAll = () => setExpanded(Object.fromEntries(filtered.map((c) => [c.id, true])));
  const collapseAll = () => setExpanded({});

  // ----- Cluster CRUD -----
  const openNewCluster = () => {
    setClusterForm({ name: "", country_ids: [] });
    setClusterDialog({ open: true, editing: null });
  };
  const openEditCluster = (c) => {
    setClusterForm({ name: c.name, country_ids: c.country_ids || (c.country_id ? [c.country_id] : []) });
    setClusterDialog({ open: true, editing: c });
  };
  const toggleCountryInForm = (id) => {
    setClusterForm((f) => {
      const set = new Set(f.country_ids);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...f, country_ids: Array.from(set) };
    });
  };
  const saveCluster = async () => {
    if (!clusterForm.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (clusterDialog.editing) {
        await api.patch(`/clusters/${clusterDialog.editing.id}`, {
          name: clusterForm.name.trim(),
          country_ids: clusterForm.country_ids,
        });
        toast.success("Cluster updated");
      } else {
        await api.post("/clusters", { name: clusterForm.name.trim(), country_ids: clusterForm.country_ids });
        toast.success("Cluster created");
      }
      setClusterDialog({ open: false, editing: null });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };
  const deleteCluster = async (c) => {
    if (c.projects_count > 0) return toast.error(`Move the ${c.projects_count} language(s) out first`);
    if (!window.confirm(`Delete cluster "${c.name}"?`)) return;
    try {
      await api.delete(`/clusters/${c.id}`);
      toast.success("Cluster deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  // ----- Language project (new) -----
  const openNewLanguage = (clusterId) => {
    setLangForm({ name: "", iso_code: "", lang_status: "Active", start_date: "", end_date: "", details: "" });
    setLangDialog({ open: true, clusterId, mode: "new" });
  };
  const saveLanguage = async () => {
    if (!langForm.name.trim()) return toast.error("Language name is required");
    setSaving(true);
    try {
      const payload = { ...langForm, name: langForm.name.trim(), cluster_id: langDialog.clusterId };
      // Strip empty strings so server treats as unset
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/language-projects", payload);
      toast.success("Language project created");
      setLangDialog({ open: false, clusterId: null, mode: "new" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  // ----- Move language between clusters -----
  const openMove = (project, currentClusterName) => {
    setMoveTarget("");
    setMoveDialog({ open: true, projectId: project.id, currentCluster: currentClusterName, projectName: project.name });
  };
  const doMove = async () => {
    if (!moveTarget) return toast.error("Pick a target cluster");
    setSaving(true);
    try {
      await api.patch(`/language-projects/${moveDialog.projectId}/cluster`, { cluster_id: moveTarget });
      toast.success("Language moved");
      setMoveDialog({ open: false, projectId: null, currentCluster: null, projectName: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  // ----- Bulk assign unassigned languages -----
  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAllUnassigned = () => {
    const filteredUnassigned = filterUnassigned();
    setSelected(new Set(filteredUnassigned.map((p) => p.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkAssign = async () => {
    if (!bulkTarget) return toast.error("Pick a target cluster");
    if (selected.size === 0) return toast.error("Select languages to assign");
    setSaving(true);
    try {
      let ok = 0;
      for (const pid of selected) {
        try {
          await api.patch(`/language-projects/${pid}/cluster`, { cluster_id: bulkTarget });
          ok += 1;
        } catch (e) { /* ignore per-item errors */ }
      }
      toast.success(`Assigned ${ok} language${ok === 1 ? "" : "s"}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const filterUnassigned = () => {
    return unassignedLangs.filter((p) => {
      if (regionFilter && p.region_id !== regionFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!p.name.toLowerCase().includes(s) &&
            !(p.country_name || "").toLowerCase().includes(s) &&
            !(p.iso_code || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  };

  return (
    <div className="max-w-[1600px] mx-auto px-10 py-10 fade-in-up" data-testid="clusters-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
            Admin · Hierarchy Management
          </div>
          <h1 className="serif text-5xl tracking-tight leading-none">Clusters</h1>
          <p className="text-sm text-[color:var(--ink-2)] mt-3 max-w-xl">
            Define cluster projects and assign languages to them. Only administrators can add,
            edit, or remove clusters.
          </p>
        </div>
        <button className="btn-primary flex items-center gap-2 text-sm" onClick={openNewCluster} data-testid="add-cluster-btn">
          <Plus size={14} /> Add Cluster
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="flex-1 min-w-64 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <input
            className="input-flat pl-9"
            placeholder="Search clusters or countries…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="clusters-search"
          />
        </div>
        <select className="input-flat w-48" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} data-testid="clusters-region-filter">
          <option value="">All Regions</option>
          {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <button className="btn-ghost text-xs" onClick={expandAll} data-testid="expand-all">Expand all</button>
        <button className="btn-ghost text-xs" onClick={collapseAll} data-testid="collapse-all">Collapse all</button>
      </div>

      {loading ? (
        <div className="mono text-xs text-[color:var(--ink-2)]">Loading…</div>
      ) : (
        <>
          {/* Unassigned Languages panel */}
          {unassignedLangs.length > 0 && (
            <div className="hairline bg-white mb-6 border-l-4" style={{ borderLeftColor: "var(--amber)" }} data-testid="unassigned-panel">
              <button
                onClick={() => setUnassignedOpen((o) => !o)}
                className="w-full flex items-center justify-between px-5 py-4 gap-4 text-left"
                data-testid="toggle-unassigned"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {unassignedOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <Inbox size={20} className="text-[color:var(--amber)]" strokeWidth={1.75} />
                  <div>
                    <div className="serif text-2xl tracking-tight">Unassigned Languages</div>
                    <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mt-1">
                      {filterUnassigned().length} language{filterUnassigned().length === 1 ? "" : "s"} without a cluster
                      {selected.size > 0 && <> · <span className="text-[color:var(--ink)]">{selected.size} selected</span></>}
                    </div>
                  </div>
                </div>
              </button>

              {unassignedOpen && (
                <div className="border-t border-[color:var(--border)] px-5 py-4 bg-[color:var(--surface-2)]">
                  {/* Bulk action bar */}
                  <div className="flex flex-wrap gap-2 items-center mb-4">
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={selectAllUnassigned} data-testid="select-all-unassigned">
                      <CheckSquare size={12} /> Select all filtered
                    </button>
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={clearSelection} data-testid="clear-selection" disabled={selected.size === 0}>
                      <Square size={12} /> Clear
                    </button>
                    <div className="flex-1" />
                    <select
                      className="input-flat text-sm py-1.5"
                      style={{ width: 320 }}
                      value={bulkTarget}
                      onChange={(e) => setBulkTarget(e.target.value)}
                      disabled={clusters.length === 0}
                      data-testid="bulk-target"
                    >
                      <option value="">
                        {clusters.length === 0 ? "Create a cluster first…" : "Assign selected to…"}
                      </option>
                      {clusters.map((cc) => {
                        const cnames = (cc.country_names && cc.country_names.length
                          ? cc.country_names
                          : cc.country_name ? [cc.country_name] : []);
                        const label = cnames.length === 0
                          ? cc.name + " · any country"
                          : cnames.length <= 2
                            ? cnames.join(", ") + " · " + cc.name
                            : `${cnames[0]} +${cnames.length - 1} · ${cc.name}`;
                        return <option key={cc.id} value={cc.id}>{label}</option>;
                      })}
                    </select>
                    <button
                      className="btn-primary text-xs"
                      onClick={bulkAssign}
                      disabled={saving || selected.size === 0 || !bulkTarget}
                      data-testid="bulk-assign-btn"
                    >
                      {saving ? "Assigning…" : `Assign ${selected.size || ""}`}
                    </button>
                  </div>

                  {filterUnassigned().length === 0 ? (
                    <div className="text-sm text-[color:var(--ink-2)] italic p-4 text-center">
                      Nothing matches your filter.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto pr-1">
                      {filterUnassigned().map((p) => {
                        const isSel = selected.has(p.id);
                        return (
                          <label
                            key={p.id}
                            className={`border bg-white px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-[color:var(--bg)] ${isSel ? "border-[color:var(--ink)]" : "border-[color:var(--border)]"}`}
                            data-testid={`unassigned-${p.name}`}
                          >
                            <input
                              type="checkbox"
                              className="accent-[color:var(--ink)]"
                              checked={isSel}
                              onChange={() => toggleSelect(p.id)}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">{p.name}</div>
                              <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)] flex items-center gap-2 truncate">
                                <span>{p.country_name || "—"}</span>
                                {p.iso_code && <span>· {p.iso_code}</span>}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {clusters.length === 0 && unassignedLangs.length > 0 && (
            <div className="hairline bg-white p-6 mb-6 text-center" data-testid="no-clusters-hint">
              <div className="serif text-2xl mb-1">No clusters yet</div>
              <div className="text-sm text-[color:var(--ink-2)]">
                Click <em className="font-semibold">Add Cluster</em> to create your first one, then assign languages above.
              </div>
            </div>
          )}

          <div className="space-y-2">
          {filtered.map((c) => {
            const open = !!expanded[c.id];
            const regionsLabel = (c.region_names && c.region_names.length ? c.region_names : (c.region_name ? [c.region_name] : [])).join(", ") || "Global";
            const countriesLabel = c.country_names && c.country_names.length
              ? (c.country_names.length <= 3 ? c.country_names.join(", ") : `${c.country_names.slice(0, 2).join(", ")} +${c.country_names.length - 2}`)
              : "any country";
            return (
              <div key={c.id} className="hairline bg-white" data-testid={`cluster-${c.name}`}>
                <div className="flex items-center justify-between px-5 py-4 gap-4 flex-wrap">
                  <button onClick={() => toggle(c.id)} className="flex items-start gap-3 min-w-0 flex-1 text-left" data-testid={`toggle-${c.name}`}>
                    {open ? <ChevronDown size={18} className="mt-1 shrink-0" /> : <ChevronRight size={18} className="mt-1 shrink-0" />}
                    <div className="min-w-0">
                      <div className="serif text-2xl tracking-tight truncate">{c.name}</div>
                      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mt-1 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><Globe2 size={10} /> {regionsLabel}</span>
                        <span>·</span>
                        <span title={(c.country_names || []).join(", ")}>{countriesLabel}</span>
                        <span>·</span>
                        <span className="flex items-center gap-1"><Languages size={10} /> {c.projects_count} languages</span>
                        <span>·</span>
                        <span>Avg {fmt(c.avg_progress)}</span>
                      </div>
                    </div>
                  </button>
                  <div className="flex gap-2 shrink-0">
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => openNewLanguage(c.id)} data-testid={`add-language-${c.name}`}>
                      <Plus size={12} /> Add language
                    </button>
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => openEditCluster(c)} data-testid={`edit-cluster-${c.name}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      className="btn-ghost text-xs flex items-center gap-1"
                      style={{ color: "var(--behind)" }}
                      onClick={() => deleteCluster(c)}
                      data-testid={`delete-cluster-${c.name}`}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="border-t border-[color:var(--border)] px-5 py-4 bg-[color:var(--surface-2)]">
                    {c.languages.length === 0 ? (
                      <div className="text-sm text-[color:var(--ink-2)] italic mb-2">
                        No languages yet. Click <em>Add language</em> to create one.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {c.languages.map((l) => (
                          <div key={l.id} className="border border-[color:var(--border)] bg-white px-3 py-2 flex items-center justify-between gap-2" data-testid={`lang-in-cluster-${l.name}`}>
                            <div className="min-w-0">
                              <Link to={`/projects/${l.id}`} className="text-sm font-medium hover:underline block truncate">{l.name}</Link>
                              <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">
                                {l.iso_code || "no iso"}
                              </div>
                            </div>
                            <button className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]" onClick={() => openMove(l, c.name)} title="Move to another cluster" data-testid={`move-${l.name}`}>
                              <Move size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && clusters.length > 0 && (
            <div className="mono text-sm text-[color:var(--ink-2)] p-8 text-center hairline bg-white">
              No clusters match your filter.
            </div>
          )}
          </div>
        </>
      )}

      {/* Cluster create/edit dialog */}
      <Dialog open={clusterDialog.open} onOpenChange={(o) => setClusterDialog({ open: o, editing: null })}>
        <DialogContent data-testid="cluster-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">
              {clusterDialog.editing ? "Edit Cluster" : "New Cluster"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mono text-[10px] uppercase tracking-widest block mb-1">Cluster Name</label>
              <input
                className="input-flat"
                value={clusterForm.name}
                onChange={(e) => setClusterForm({ ...clusterForm, name: e.target.value })}
                placeholder="e.g., Chin Cluster, Trans-Himalayan Cluster"
                data-testid="cluster-form-name"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="mono text-[10px] uppercase tracking-widest">
                  Countries in scope <span className="text-[color:var(--ink-3)] normal-case">(optional — pick any across regions)</span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
                    onClick={() => setClusterForm({ ...clusterForm, country_ids: countries.map((c) => c.id) })}
                  >
                    Select all
                  </button>
                  <span className="text-[color:var(--ink-3)]">·</span>
                  <button
                    type="button"
                    className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
                    onClick={() => setClusterForm({ ...clusterForm, country_ids: [] })}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="border border-[color:var(--border-strong)] max-h-64 overflow-y-auto p-3 bg-[color:var(--surface)]" data-testid="country-multi">
                {regions.map((r) => (
                  <div key={r.id} className="mb-3 last:mb-0">
                    <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1.5">
                      {r.name}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {countries.filter((c) => c.region_id === r.id).map((c) => {
                        const checked = clusterForm.country_ids.includes(c.id);
                        return (
                          <label
                            key={c.id}
                            className={`flex items-center gap-2 px-2 py-1 cursor-pointer text-sm border ${checked ? "border-[color:var(--ink)] bg-[color:var(--surface-2)]" : "border-transparent hover:border-[color:var(--border)]"}`}
                            data-testid={`country-check-${c.name}`}
                          >
                            <input
                              type="checkbox"
                              className="accent-[color:var(--ink)]"
                              checked={checked}
                              onChange={() => toggleCountryInForm(c.id)}
                            />
                            <span className="truncate">{c.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mono text-[10px] text-[color:var(--ink-2)] mt-2">
                {clusterForm.country_ids.length === 0
                  ? "Cluster will span no specific country (global). You can still add languages one by one."
                  : `${clusterForm.country_ids.length} countr${clusterForm.country_ids.length === 1 ? "y" : "ies"} selected`}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost text-sm" onClick={() => setClusterDialog({ open: false, editing: null })}>Cancel</button>
            <button className="btn-primary text-sm" onClick={saveCluster} disabled={saving} data-testid="cluster-form-submit">
              {saving ? "Saving…" : clusterDialog.editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New language project dialog */}
      <Dialog open={langDialog.open} onOpenChange={(o) => setLangDialog({ ...langDialog, open: o })}>
        <DialogContent data-testid="lang-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">Add Language to Cluster</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mono text-[10px] uppercase tracking-widest block mb-1">Language Name *</label>
              <input
                className="input-flat"
                value={langForm.name}
                onChange={(e) => setLangForm({ ...langForm, name: e.target.value })}
                placeholder="e.g., Khumi"
                data-testid="lang-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">ISO Code</label>
                <input
                  className="input-flat mono uppercase"
                  value={langForm.iso_code}
                  onChange={(e) => setLangForm({ ...langForm, iso_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., CNH"
                  maxLength={5}
                  data-testid="lang-form-iso"
                />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">Status</label>
                <select
                  className="input-flat"
                  value={langForm.lang_status}
                  onChange={(e) => setLangForm({ ...langForm, lang_status: e.target.value })}
                  data-testid="lang-form-status"
                >
                  <option value="Active">Active</option>
                  <option value="Starting Soon">Starting Soon</option>
                  <option value="Pending">Pending</option>
                  <option value="Suspended">Suspended</option>
                  <option value="Dropped">Dropped</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">Started</label>
                <input
                  type="date"
                  className="input-flat"
                  value={langForm.start_date}
                  onChange={(e) => setLangForm({ ...langForm, start_date: e.target.value })}
                  data-testid="lang-form-start"
                />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">Expected End</label>
                <input
                  type="date"
                  className="input-flat"
                  value={langForm.end_date}
                  onChange={(e) => setLangForm({ ...langForm, end_date: e.target.value })}
                  data-testid="lang-form-end"
                />
              </div>
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest block mb-1">Notes</label>
              <textarea
                className="input-flat"
                rows={2}
                value={langForm.details}
                onChange={(e) => setLangForm({ ...langForm, details: e.target.value })}
                data-testid="lang-form-details"
              />
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost text-sm" onClick={() => setLangDialog({ ...langDialog, open: false })}>Cancel</button>
            <button className="btn-primary text-sm" onClick={saveLanguage} disabled={saving} data-testid="lang-form-submit">
              {saving ? "Saving…" : "Create Language"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move language dialog */}
      <Dialog open={moveDialog.open} onOpenChange={(o) => setMoveDialog({ ...moveDialog, open: o })}>
        <DialogContent data-testid="move-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">Move Language</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              Move <span className="font-semibold">{moveDialog.projectName}</span> from{" "}
              <span className="mono text-xs">{moveDialog.currentCluster}</span> to:
            </div>
            <select
              className="input-flat"
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
              data-testid="move-target"
            >
              <option value="">— Select target cluster —</option>
              {clusters.map((cc) => {
                const cnames = (cc.country_names && cc.country_names.length
                  ? cc.country_names
                  : cc.country_name ? [cc.country_name] : []);
                const label = cnames.length === 0
                  ? cc.name + " · any country"
                  : cnames.length <= 2
                    ? cnames.join(", ") + " · " + cc.name
                    : `${cnames[0]} +${cnames.length - 1} · ${cc.name}`;
                return <option key={cc.id} value={cc.id}>{label}</option>;
              })}
            </select>
          </div>
          <DialogFooter>
            <button className="btn-ghost text-sm" onClick={() => setMoveDialog({ ...moveDialog, open: false })}>Cancel</button>
            <button className="btn-primary text-sm" onClick={doMove} disabled={saving} data-testid="move-submit">
              {saving ? "Moving…" : "Move"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
